import { Module, VuexModule, Mutation, Action } from 'vuex-module-decorators';
import ArticleService from '@/Services/ArticlesService';
import Article from '@/models/Article';
import { isErrorResponse } from '@/utils/typeGuards';

@Module({ namespaced: true })
export default class ArticleModule extends VuexModule {
    Articles: Article[] = [];
    error: string | null = null;
    Article: Article | null = null;

    @Mutation
    setArticles(Articles: Article[]) {
        this.Articles = Articles;
    }

    @Mutation
    setArticle(Article: Article) {
        this.Article = Article;
    }

    @Mutation
    setError(error: string | null) {
        this.error = error;
    }

    @Action
    async fetchArticles(params: string) { 
        try {
            const response = await ArticleService.getAll(params);

            if (isErrorResponse(response)) {
                this.context.commit('setError', response.message || 'An error occurred while fetching articles.');
                return null; // Return null on error
            } else {
                const { data, totalRows, recordsFiltered, recordsTotal, draw } = response;
                this.context.commit('setArticles', data);
                
                // Retourner le format DataTable complet
                return { 
                    data, 
                    totalRows, 
                    recordsFiltered, 
                    recordsTotal, 
                    draw 
                };
            }
        } catch (error: any) {
            this.context.commit('setError', error.message || 'An error occurred while fetching articles.');
            return null; // Return null on error
        }
    }

    @Action
    async fetchArticlesConsomme(url: string) {
        try {
            const response = await ArticleService.getAllConsomme(url);
            
            // Détecter si c'est un export
            const isExport = url.includes('export=excel') || url.includes('export=csv');
            
            if (isExport) {
                return response;
            }

            if (isErrorResponse(response)) {
                this.context.commit('setError', response.message || 'An error occurred while fetching articles.');
                return null; // Return null on error
            } else {
                const { data, totalRows, recordsFiltered, recordsTotal, draw } = response;
                this.context.commit('setArticles', data);
                
                // Retourner le format DataTable complet
                return { 
                    data, 
                    totalRows, 
                    recordsFiltered, 
                    recordsTotal, 
                    draw 
                };
            }
        } catch (error: any) {
            this.context.commit('setError', error.message || 'An error occurred while fetching articles.');
            return null; // Return null on error
        }
    }



    @Action
    async getById(id: number) {
        try {
            const response = await ArticleService.getById(id);

            if (isErrorResponse(response)) {
                this.context.commit('setError', response.message || 'Article non trouvé');
                return null;
            } else {
                this.context.commit('setArticle', response);
                this.context.commit('setError', null);
                return response;
            }
        } catch (error: any) {
            this.context.commit('setError', error.message || 'Une erreur est survenue');
            return null;
        }
    }

    @Action
    async supprimerById(id: number) {
        try {
            const response = await ArticleService.supprimer(id);

            if (isErrorResponse(response)) {
                this.context.commit('setError', response.message || 'Erreur lors de la suppression');
                return null;
            } else {
                this.context.commit('setError', null);
                return response; // Retourne le status code
            }
        } catch (error: any) {
            this.context.commit('setError', error.message || 'Une erreur est survenue');
            return null;
        }
    }

    @Action
    async addArticle(article: Article) {
        try {
            const response = await ArticleService.create(article);
            
            if (isErrorResponse(response)) {
                this.context.commit('setError', response.message || 'Erreur lors de la création');
                return null;
            } else {
                this.context.commit('setError', null);
                return response; // Retourne le status code
            }
        } catch (error: any) {
            this.context.commit('setError', error.message || 'Une erreur est survenue');
            return null;
        }
    }

    @Action
    async addArticlesWithImport(file: File[]) {
        try {
            const response = await ArticleService.createwithImport(file);
            
            if (isErrorResponse(response)) {
                this.context.commit('setError', response.message || 'Erreur lors de l\'import');
                return null;
            } else {
                this.context.commit('setError', null);
                return response; // Retourne le status code
            }
        } catch (error: any) {
            this.context.commit('setError', error.message || 'Une erreur est survenue');
            return null;
        }
    }

    @Action
    async updateArticle({ id, article } : { id: number; article: Article }) {
        try {
            const response = await ArticleService.update({id, article});
            
            if (isErrorResponse(response)) {
                this.context.commit('setError', response.message || 'Erreur lors de la mise à jour');
                return null;
            } else {
                this.context.commit('setError', null);
                return response; // Retourne le status code
            }
        } catch (error: any) {
            this.context.commit('setError', error.message || 'Une erreur est survenue');
            return null;
        }
    }

    @Action
    async extractArticles(params: string) {
        try {
            const response = await ArticleService.extractAll(params);
            
            if (isErrorResponse(response)) {
                this.context.commit('setError', response.message || 'Erreur lors de l\'export');
                return null;
            } else {
                this.context.commit('setError', null);
                return response; // Retourne la réponse blob
            }
        } catch (error: any) {
            this.context.commit('setError', error.message || 'Une erreur est survenue');
            return null;
        }
    }

    @Action
    async extractArticlesConsommes(params: string) {
        try {
            const response = await ArticleService.extractAllConsommes(params);
            
            if (isErrorResponse(response)) {
                this.context.commit('setError', response.message || 'Erreur lors de l\'export');
                return null;
            } else {
                this.context.commit('setError', null);
                return response; // Retourne la réponse blob
            }
        } catch (error: any) {
            this.context.commit('setError', error.message || 'Une erreur est survenue');
            return null;
        }
    }

    

    get getAll() {
        return this.Articles;
    }

    get getArticle() {
        return this.Article;
    }
}
